import { Box, Button, Grid, GridItem } from "@chakra-ui/react";
import React, { ReactElement, useState, useEffect, ReactNode } from "react";
import { useAuthState } from "../../context/AuthState";
import { Break } from "../styled/Break";
import { Label, Title } from "../styled/Text";
import { BackAndNext } from "./BackAndNext";
import { Card, CardData } from "./Card";
import { Hide } from "../common/Hide";
import { Formik, Form } from "formik";
import { isDesktopHook } from "../../Hooks";
import { AutocompleteField } from "../common/form_fields/AutocompleteField";
import { enumToString } from "../../Utils";
import { monsterTags, PowerLevel } from "../../constants/Monster";
import { SuggestionField } from "../common/form_fields/SuggestionField";
import { useApiClient } from "../../context/ApiClient";
import { getMonsterNamesPublished } from "../../api/monster";

export function CardGridMonster({ title, apiMethod, showFilters, dataConverter, contentType, collection }: GridProp) {
  const colorProp = {
    secondaryColor: "monster.secondary",
    primaryColor: "monster.primary",
  };
  const isDesktop = isDesktopHook();

  const apiClient = useApiClient().getClient();
  const [nameOptions, setNameOptions] = useState<string[]>([]);

  useEffect(() => {
    getMonsterNamesPublished(apiClient).then((resp) => {
      setNameOptions(resp.data);
    });
  }, []);

  return (
    <CardGrid
      colorProp={colorProp}
      title={title}
      apiMethod={apiMethod}
      showFilters={showFilters}
      dataConverter={dataConverter}
      contentType={contentType}
      collection={collection}
    >
      <Grid w="95vw" gap={6} templateColumns={{ lg: "repeat(10, 1fr)}", md: "repeat(3, 1fr)" }}>
        <GridItem colSpan={3}>
          <Label text="Power Level" />
          <AutocompleteField
            name="powerLevel"
            options={enumToString(PowerLevel).map((level) => {
              return { label: level, value: level };
            })}
            multiple={true}
            borderColor="monster.secondary"
            borderColorHover="monster.secondaryHover"
            tagScheme="red"
          />
        </GridItem>
        <GridItem colSpan={3}>
          <Label text="Tags" />
          <AutocompleteField
            name="tags"
            options={monsterTags.map((tag) => {
              return { label: tag, value: tag };
            })}
            multiple={true}
            borderColor="monster.primary"
            borderColorHover="monster.primaryHover"
            tagScheme="blue"
          />
        </GridItem>
        <GridItem colSpan={3}>
          <Label text="Name" />
          <SuggestionField name="name" borderColor="monster.primary" height="7.3vh" options={nameOptions} />
        </GridItem>
        <GridItem colSpan={1}>
          <Button
            m={isDesktop ? "5vh 0 0 0" : "0 0 0 32vw"}
            background="monster.primary"
            color="white"
            type="submit"
            _hover={{ background: "monster.primaryHover" }}
          >
            Search
          </Button>
        </GridItem>
      </Grid>
    </CardGrid>
  );
}

export function CardGrid({ title, apiMethod, showFilters, dataConverter, contentType, colorProp, collection, children }: FullProp): ReactElement {
  const [data, setData] = useState<[]>([]);
  const [filterValues, setFilterValues] = useState({});
  const [after, setAfter] = useState(undefined);
  const [before, setBefore] = useState(undefined);
  const pageSize = isDesktopHook() ? 10 : 100;
  const [paginationOptions, setPaginationOptions] = useState({ size: pageSize });
  const authState = useAuthState();

  useEffect(() => {
    apiMethod(filterValues, paginationOptions).then((response) => {
      setData(response.data);
      setAfter(response.after);
      setBefore(response.before);
    });
  }, [authState, filterValues, paginationOptions]);

  const onSubmitTableFilters = (values: any) => {
    setFilterValues(values);
  };

  const cards = data.map((anyData, idx) => {
    const cardData = dataConverter(anyData);
    return (
      <Card
        cardData={cardData}
        contentType={contentType}
        key={`Card ${cardData.name}${idx}`}
        primaryTagScheme={colorProp.primaryColor}
        secondaryTagScheme={"monster.secondary"}
        hoverBorderColor={colorProp.secondaryColor}
        collection={collection}
      />
    );
  });

  return (
    <Box>
      <Title text={title} />
      <Box margin="0 5vw 0 5vw">
        <Hide hide={!showFilters}>
          <Box mb="1vh">
            <Formik initialValues={{}} onSubmit={onSubmitTableFilters}>
              <Form>{children}</Form>
            </Formik>
          </Box>
        </Hide>
        <Grid
          templateColumns={{
            base: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(5, 1fr)",
          }}
        >
          {cards}
        </Grid>
        <Break height="1vh" />
        <BackAndNext
          isBackHidden={before === undefined}
          onClickBack={() => setPaginationOptions({ size: pageSize, before: before })}
          onClickNext={() => setPaginationOptions({ size: pageSize, after: after })}
          backBackground={colorProp.primaryColor}
          nextBackground={colorProp.secondaryColor}
        />
      </Box>
    </Box>
  );
}

interface GridProp {
  title: string;
  apiMethod: (filterValues: any, paginationOptions: any) => Promise<any>;
  showFilters: boolean;
  dataConverter: (data: any) => CardData;
  contentType: string;
  collection: string;
}

interface ColorProp {
  primaryColor: string;
  secondaryColor: string;
}

interface FullProp extends GridProp {
  colorProp: ColorProp;
  children: ReactNode;
}
